import { fallbackLng } from "@/app/i18n/settings";
import type { LocaleEnum } from "@/types/utils/locale";
import dayjs from "@/utils/dayjs";
import { LocalStorageItemEnum } from "@/utils/enums/LocalStorageItemEnum";
import { SessionStorageItemEnum } from "@/utils/enums/SessionStorageItemEnum";
import { UtmChanelEnum } from "@/utils/enums/UtmChanelEnum";

import { formatDateForQuery } from "../dateHelper/dateConvertFormatUtills";
import {
  findNearestWeekend,
  getNextTwoWeeksMondayAndDaysAfter,
} from "../dateHelper/dateFindingUtills";
import { setLocalStorageItem } from "../localStorageItemHelper/localStorageItemUtills";
import { filterEmptyValues } from "../mapHelper/mapHelperUtils";
import { setSessionStorageItem } from "../sessionStorageItemHelper/sessionStorageItemUtils";
import { createQueryStringWithSession } from "../validateHelper/validateUtils";

export function queryStringToObject(
  queryString: string,
): Record<string, string | string[]> {
  // eslint-disable-next-line no-param-reassign
  queryString = queryString.replace(/^\?/, "");

  // Splitting the query string into key-value pairs
  const pairs = queryString.split("&");

  // Initializing the result object
  const result: Record<string, string | string[]> = {};

  // Iterating through key-value pairs and populating the result object
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < pairs.length; i++) {
    const pair = pairs[i]!.split("=");

    // Check if the pair has a key and a value
    if (pair.length === 2) {
      let key = decodeURIComponent(pair[0]!);
      const value = decodeURIComponent(pair[1] || "");

      // Handling arrays by checking for square brackets in the key
      if (key.indexOf("[]") !== -1) {
        key = key.replace("[]", "");
        result[key] = result[key] || [];
        (result[key] as string[]).push(value);
      } else {
        result[key] = value;
      }
    }
  }

  return result;
}

const replaceQueryParams = (
  url: string,
  key: string,
  value: string,
): string => {
  const urlObject = new URL(url);
  const pageTypeParam = urlObject.searchParams.get(key);

  if (pageTypeParam) {
    urlObject.searchParams.set(key, value);
  }

  return urlObject.toString();
};

export const addQueryParamAndPushNewUrl = (
  paramName: string,
  paramValue: string,
): void => {
  const currentUrl = window.location.href;

  let newUrl: string;
  if (currentUrl.indexOf(paramName) === -1) {
    const separator = currentUrl.indexOf("?") !== -1 ? "&" : "?";

    newUrl = `${currentUrl + separator}${paramName}=${paramValue}`;
  } else {
    newUrl = replaceQueryParams(currentUrl, paramName, paramValue);
  }

  window.location.href = newUrl;
};

export const isFeatureMainPage = (
  href?: string,
  maxOccurrences: number = 2,
) => {
  if (!href) return false;

  const slashCount = href.split("/").length - 1;

  if (maxOccurrences > slashCount) {
    return false;
  }

  return slashCount <= maxOccurrences;
};

export const getDefaultHotelDateRoute = (
  slugPath: string,
  path: string,
  hotelId?: number,
) => {
  const hotelPath = `hotels`;

  if (!hotelId) {
    return `${path}/${hotelPath}/${slugPath}`;
  }

  const nearestHoliday = findNearestWeekend(dayjs());

  const dateCheckIn = formatDateForQuery(nearestHoliday.nearestSaturday);
  const dateCheckOut = formatDateForQuery(nearestHoliday.nearestSunday);

  const queryParamHotelDetail = {
    checkIn: dateCheckIn,
    checkOut: dateCheckOut,
    hotel: hotelId,
    adults: 2,
    rooms: 1,
  };

  const queryStringHotelDetailWithSession = createQueryStringWithSession(
    queryParamHotelDetail,
  );

  const params = `${path}/${hotelPath}/${slugPath}?${queryStringHotelDetailWithSession}`;

  return params;
};

export const generateLinkWightSession = ({
  hotelId = 0,
  checkIn = "",
  checkOut = "",
  url = "",
}: {
  hotelId: number;
  checkIn?: string;
  checkOut?: string;
  url: string;
}) => {
  const dateFormat = "YYYY-MM-DD";
  let checkInV = checkIn;
  let checkOutV = checkOut;
  if (!checkInV || !checkOutV) {
    const { nextTDays, nextTwoWeeksMonday } =
      getNextTwoWeeksMondayAndDaysAfter(dayjs());
    checkInV = nextTwoWeeksMonday.format(dateFormat);
    checkOutV = nextTDays.format(dateFormat);
  }
  const params = {
    checkIn: checkInV,
    checkOut: checkOutV,
    hotel: hotelId,
    adults: 2,
    rooms: 1,
  };
  const removeEmpty = filterEmptyValues(params);
  if (Object.keys(removeEmpty).length < 5) return url;
  const session = createQueryStringWithSession(params);
  return `hotels/${url}?${session}`;
};

export const getCustomHotelDateRoute = (
  hotelId: number,
  slugPath: string,
  checkInDate?: string,
  checkOutDate?: string,
  adultsNum?: number | string,
  roomsNum?: number,
  path?: string,
) => {
  const nearestHoliday = findNearestWeekend(dayjs());

  const dateCheckIn = formatDateForQuery(nearestHoliday.nearestSaturday);
  const dateCheckOut = formatDateForQuery(nearestHoliday.nearestSunday);

  const queryParamHotelDetail = {
    checkIn: checkInDate || dateCheckIn,
    checkOut: checkOutDate || dateCheckOut,
    hotel: hotelId,
    adults: adultsNum || 2,
    rooms: roomsNum || 1,
  };

  const queryStringHotelDetailWithSession = createQueryStringWithSession(
    queryParamHotelDetail,
  );

  const params = `hotels/${slugPath}?${queryStringHotelDetailWithSession}`;

  return path ? `${path}/${params}` : params;
};

type Valuable<T> = {
  [K in keyof T as T[K] extends null | undefined ? never : K]: T[K];
};

export function getValuable<
  // eslint-disable-next-line @typescript-eslint/ban-types
  T extends {},
  V = Valuable<T>,
>(obj: T): V {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) =>
        !(
          (typeof v === "string" && !v.length) ||
          v === null ||
          typeof v === "undefined"
        ),
    ),
  ) as V;
}

export function processUrl(url: string): string {
  let newUrl = url;
  try {
    // Ensure the URL starts with a leading /
    if (newUrl.charAt(0) !== "/") {
      newUrl = `/${newUrl}`;
    }

    // Extract pathname from URL string
    const urlParts = newUrl.split("?");
    let pathname = urlParts[0];

    // Check if pathname is defined
    if (pathname === undefined) {
      return newUrl;
    }

    // Split pathname into segments
    const urlSegments = pathname.split("/");

    // 1. Remove leading // if present
    if (urlSegments[1] === "") {
      urlSegments.splice(1, 1);
    }

    // Remove duplicate language path segments
    const filteredSegments: string[] = [];
    urlSegments.forEach((segment, index) => {
      if (index === 0 || segment !== urlSegments[index - 1]) {
        filteredSegments.push(segment);
      }
    });

    // Reconstruct the URL
    pathname = filteredSegments.join("/");
    const processedUrl = urlParts[0] + (urlParts[1] ? `?${urlParts[1]}` : "");
    return processedUrl;
  } catch (er) {
    return newUrl; // Return the original URL if it's invalid
  }
}

export function convertToString(value: string | string[]): string {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return value;
}

export function replaceAndModifySegment(
  inputString: string,
  oldSegment: string,
  newSegment: string,
  removePrefix: boolean = false,
): string {
  if (removePrefix) {
    // Find the index of the segment we want to replace
    const segmentIndex = inputString.indexOf(oldSegment);
    if (segmentIndex !== -1) {
      // eslint-disable-next-line no-param-reassign
      inputString = inputString.substring(segmentIndex);
    }
  }

  // eslint-disable-next-line no-param-reassign
  inputString = inputString.replace(oldSegment, newSegment);

  return inputString;
}

export function removeDuplicateSegments(inputString: string): string {
  const segments = inputString.split("/").filter(Boolean);
  const uniqueSegments: string[] = [];
  segments.forEach((segment, index) => {
    if (index === 0 || segment !== segments[index - 1]) {
      uniqueSegments.push(segment);
    }
  });
  return `/${uniqueSegments.join("/")}`;
}

export function fixUrl(url: string): string {
  const questionMarkIndex = url.indexOf("?");

  // Check if there is a second '?' in the URL
  if (questionMarkIndex !== -1) {
    // Replace all subsequent '?' with '&'
    const firstPart = url.slice(0, questionMarkIndex + 1);
    const rest = url.slice(questionMarkIndex + 1).replace(/\?/g, "&");
    return firstPart + rest;
  }

  // Return the URL as is if there's only one or no '?' present
  return url;
}

export const getIsUtmByGotherApp = (utmId: string): boolean => {
  return (
    utmId === UtmChanelEnum.GOTHER_APP_ANDROID ||
    utmId === UtmChanelEnum.GOTHER_APP_IOS
  );
};

const getNextRoundedTime = (interval: number = 15): string => {
  const now: Date = new Date();

  const year: number = now.getFullYear();
  const month: number = now.getMonth(); // 0-based
  const day: number = now.getDate();
  const hour: number = now.getHours();
  const minutes: number = now.getMinutes();

  const roundedMinutes: number = Math.ceil(minutes / interval) * interval;

  const roundedDate: Date = new Date(year, month, day, hour, 0, 0);
  roundedDate.setMinutes(roundedMinutes);

  const pad = (num: number): string => String(num).padStart(2, "0");

  const formatted: string = `${roundedDate.getFullYear()}-${pad(roundedDate.getMonth() + 1)}-${pad(roundedDate.getDate())} ${pad(roundedDate.getHours())}:${pad(roundedDate.getMinutes())}:${pad(roundedDate.getSeconds())}`;

  return formatted;
};

export const appendCacheParamsToUrl = ({
  url,
  lang = fallbackLng,
  currency,
  minuteCache,
  utmId,
  dataTokenJwt,
  isCache = true,
}: {
  url: string;
  lang?: LocaleEnum;
  currency?: string;
  minuteCache?: number;
  utmId?: string;
  dataTokenJwt?: string;
  isCache?: boolean;
}): string => {
  const urlObj = new URL(url, "http://dummy-base"); // Base required for relative URLs

  const params = urlObj.searchParams;

  if (lang) params.set("lang", lang);
  if (currency) params.set("currency", currency);
  if (utmId) params.set("utmId", utmId);
  if (dataTokenJwt) params.set("dataTokenJwt", dataTokenJwt);

  if (minuteCache && isCache) {
    const cacheTime = getNextRoundedTime(minuteCache);
    if (cacheTime) params.set("cacheTime", cacheTime);
  }

  const path = url.split("?")[0];
  return `${path}?${params.toString()}`;
};

export const getPagePathFormUrl = (original?: boolean): string => {
  if (typeof window !== "undefined") {
    const originPath = window.location.pathname;
    const path = window.location.pathname + window.location.search;
    const pathname = processUrl(originPath);
    const pagePath = pathname.split("/").slice(2).join("/");

    if (original) {
      return path;
    }
    if (pagePath && pagePath.includes("search")) {
      return "";
    }

    return pagePath || "";
  }
  return "";
};

export const parseQueryString = (query: string) => {
  return Object.fromEntries(new URLSearchParams(query));
};

export function reloadAllPageClient(payload?: {
  isNotLoadCurrentPage?: boolean;
}) {
  if (
    payload &&
    payload.isNotLoadCurrentPage &&
    payload.isNotLoadCurrentPage === true
  ) {
    setSessionStorageItem(
      SessionStorageItemEnum.RE_TAB,
      dayjs().format("YYYY-MM-DD HH:mm:ss"),
    );
  }

  setLocalStorageItem(
    LocalStorageItemEnum.RE_TAB,
    dayjs().format("YYYY-MM-DD HH:mm:ss"),
  );
}
